/**
 * financing.ts — one canonical mirror of the backend's financing presets
 * (backend/app/services/investment_analyzer.py :: FINANCING_PRESETS, the
 * source of truth for the math).
 *
 * MOVED from web-app/src/financing.ts and DECOUPLED from state.js:
 * the original read `state.user?.investment_criteria` directly. Every
 * function here takes its inputs as parameters, so web-app, mobile,
 * and tests resolve the user's loan the same way without touching app
 * state.
 */

import type { InvestorFinancingType } from './types.js';

export const FINANCING_PRESET_DEFAULTS: Record<
  InvestorFinancingType,
  { down: number; rate: number; term: number }
> = {
  dscr: { down: 25, rate: 7.5, term: 30 },
  conventional: { down: 20, rate: 7.0, term: 30 },
  hard_money: { down: 25, rate: 11.0, term: 1 },
  cash: { down: 100, rate: 0, term: 30 },
};

export interface EffectiveLoanTerms {
  downPct: number;
  ratePct: number;
  termYears: number;
  /** Hard money is modeled as an interest-only bridge. */
  interestOnly: boolean;
}

/** Per-type loan-term overrides — the user's fine-tuned profile fields. */
export interface LoanTermOverrides {
  downPct?: number;
  ratePct?: number;
  termYears?: number;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/**
 * Resolve the user's effective loan terms: the preset for their
 * financing type, overridden field-by-field by their fine-tuned
 * down_payment_pct / interest_rate / loan_term_years (omitted for cash).
 * hard_money always resolves interestOnly — the brutal short-term math
 * is the honest signal, not a bug.
 */
export function resolveEffectiveLoan(
  type: InvestorFinancingType,
  overrides: LoanTermOverrides = {},
): EffectiveLoanTerms {
  const preset = FINANCING_PRESET_DEFAULTS[type] || FINANCING_PRESET_DEFAULTS.dscr;
  return {
    downPct: num(overrides.downPct) ?? preset.down,
    ratePct: num(overrides.ratePct) ?? preset.rate,
    termYears: num(overrides.termYears) ?? preset.term,
    interestOnly: type === 'hard_money',
  };
}

/**
 * Short honest label for a financing type, e.g. "Conventional · 20% down".
 * Hard money keeps its explicit "12-mo bridge" wording so derived
 * interest-only numbers are never misread as 30-year amortization.
 */
export function financingLabel(type: string, downPct?: number): string {
  const preset = FINANCING_PRESET_DEFAULTS[type as InvestorFinancingType];
  const names: Record<InvestorFinancingType, string> = {
    dscr: 'DSCR loan',
    conventional: 'Conventional',
    hard_money: 'Hard money',
    cash: 'Cash',
  };
  const name = names[type as InvestorFinancingType] || 'DSCR loan';
  if (type === 'cash') return 'Cash';
  if (type === 'hard_money') return 'Hard money · 12-mo bridge';
  const down = downPct ?? preset?.down ?? FINANCING_PRESET_DEFAULTS.dscr.down;
  return `${name} · ${down}% down`;
}
